import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Edit, UserPlus, Building, Package, CreditCard, GitBranch, Crown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import NewClientDialog from '@/components/Clients/NewClientDialog';
import { useClients } from '@/hooks/useClients';
import { ClientDetailsPanel } from './ClientDetailsPanel';
import { ClientModulesDialog } from './ClientModulesDialog';
import { EditClientInlineDialog } from './EditClientInlineDialog';
import { ClientUsersDialog } from './ClientUsersDialog';

interface ClientsOverviewProps {
  showAll?: boolean;
}

interface SelectedClientData {
  id: string;
  name: string;
}

export const ClientsOverview = ({ showAll = true }: ClientsOverviewProps) => {
  const [selectedClient, setSelectedClient] = useState<SelectedClientData | null>(null);
  const [isNewClientDialogOpen, setIsNewClientDialogOpen] = useState(false);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [isModulesDialogOpen, setIsModulesDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false);
  const { addClient } = useClients();

  const { data: clients, isLoading, refetch } = useQuery({
    queryKey: ['admin-clients', showAll],
    queryFn: async () => {
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(showAll ? 100 : 5);

      if (clientsError) throw clientsError;

      // Buscar todos os usuários ativos
      const { data: allUsers, error: usersError } = await supabase
        .from('users')
        .select('client_id, client_ids')
        .eq('status', 'active');

      if (usersError) throw usersError;

      // Buscar módulos ativos por cliente
      const { data: allModules, error: modulesError } = await supabase
        .from('client_modules')
        .select('client_id, module_code')
        .eq('is_active', true);

      if (modulesError) throw modulesError;

      // Buscar licenças ativas
      const { data: allLicenses, error: licensesError } = await supabase
        .from('client_licenses')
        .select('client_id, status, license_plans(name)')
        .eq('status', 'active');

      if (licensesError) throw licensesError;

      // Contar sub-clientes por cliente pai
      const subClientCounts: Record<string, number> = {};
      const parentNames: Record<string, string> = {};
      clientsData?.forEach(client => {
        if (client.parent_client_id) {
          subClientCounts[client.parent_client_id] = (subClientCounts[client.parent_client_id] || 0) + 1;
          // Map child to parent name
          const parent = clientsData.find(c => c.id === client.parent_client_id);
          if (parent) {
            parentNames[client.id] = parent.name;
          }
        }
      });

      // Contar usuários e módulos por cliente
      const clientsWithCounts = clientsData?.map(client => {
        const userCount = allUsers?.filter(user => {
          if (user.client_id === client.id) return true;
          if (user.client_ids && Array.isArray(user.client_ids)) {
            return user.client_ids.includes(client.id);
          }
          return false;
        }).length || 0;

        const moduleCount = allModules?.filter(m => m.client_id === client.id).length || 0;
        const license = allLicenses?.find(l => l.client_id === client.id);

        return {
          ...client,
          users: [{ count: userCount }],
          moduleCount,
          licensePlan: license?.license_plans?.name || null,
          licenseStatus: license?.status || null,
          subClientCount: subClientCounts[client.id] || 0,
          parentName: parentNames[client.id] || null,
          isParent: (subClientCounts[client.id] || 0) > 0,
          isSubClient: !!client.parent_client_id,
        };
      });

      return clientsWithCounts;
    }
  });

  const handleViewClient = (client: any) => {
    setSelectedClient({ id: client.id, name: client.name });
    setIsDetailsPanelOpen(true);
  };

  const handleEditClient = (client: any) => {
    setSelectedClient({ id: client.id, name: client.name });
    setIsEditDialogOpen(true);
  };

  const handleManageUsers = (client: any) => {
    setSelectedClient({ id: client.id, name: client.name });
    setIsUsersDialogOpen(true);
  };

  const handleManageModules = (client: any) => {
    setSelectedClient({ id: client.id, name: client.name });
    setIsModulesDialogOpen(true);
  };

  const handleAddClient = async (clientData: any) => {
    try {
      await addClient.mutateAsync(clientData);
      setIsNewClientDialogOpen(false);
      refetch(); // Atualiza a lista de clientes
      toast({
        title: "Cliente cadastrado",
        description: "Cliente cadastrado com sucesso!",
      });
      return true;
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao cadastrar cliente",
        variant: "destructive",
      });
      return false;
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Nome do Cliente',
      render: (client: any) => (
        <div className="flex items-center gap-2">
          <Building className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{client.name}</span>
          {client.isParent && (
            <Badge variant="outline" className="gap-1 text-xs border-amber-500 text-amber-600">
              <Crown className="h-3 w-3" />
              Pai ({client.subClientCount})
            </Badge>
          )}
          {client.isSubClient && (
            <Badge variant="outline" className="gap-1 text-xs border-blue-500 text-blue-600">
              <GitBranch className="h-3 w-3" />
              Sub de {client.parentName}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
    },
    {
      key: 'status',
      header: 'Status',
      render: (client: any) => (
        <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
          {client.status === 'active' ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'users',
      header: 'Usuários',
      render: (client: any) => (
        <Badge variant="outline">
          {client.users?.[0]?.count || 0} usuários
        </Badge>
      ),
    },
    {
      key: 'moduleCount',
      header: 'Módulos',
      render: (client: any) => (
        <Badge variant="outline" className="gap-1">
          <Package className="h-3 w-3" />
          {client.moduleCount || 0}
        </Badge>
      ),
    },
    {
      key: 'licensePlan',
      header: 'Licença',
      render: (client: any) => (
        client.licensePlan ? (
          <Badge variant="default" className="gap-1">
            <CreditCard className="h-3 w-3" />
            {client.licensePlan}
          </Badge>
        ) : (
          <Badge variant="secondary">Sem licença</Badge>
        )
      ),
    },
    {
      key: 'created_at',
      header: 'Data de Criação',
      render: (client: any) => {
        const date = new Date(client.created_at);
        return date.toLocaleDateString('pt-BR');
      },
    },
  ];

  const actions = [
    {
      icon: <Eye className="h-4 w-4" />,
      label: 'Visualizar',
      onClick: (client: any) => handleViewClient(client)
    },
    {
      icon: <Edit className="h-4 w-4" />,
      label: 'Editar',
      onClick: (client: any) => handleEditClient(client)
    },
    {
      icon: <UserPlus className="h-4 w-4" />,
      label: 'Gerenciar Usuários',
      onClick: (client: any) => handleManageUsers(client)
    },
    {
      icon: <Package className="h-4 w-4" />,
      label: 'Gerenciar Módulos',
      onClick: (client: any) => handleManageModules(client)
    }
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: showAll ? 10 : 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!showAll) {
    // Versão resumida para o dashboard
    return (
      <div className="space-y-3">
        {clients?.slice(0, 5).map((client) => (
          <div key={client.id} className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <Building className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{client.name}</p>
                <p className="text-sm text-muted-foreground">{client.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Package className="h-3 w-3" />
                {client.moduleCount || 0}
              </Badge>
              <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                {client.status === 'active' ? 'Ativo' : 'Inativo'}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => handleViewClient(client)}>
                <Eye className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Clientes ({clients?.length || 0})</h3>
        <Button onClick={() => setIsNewClientDialogOpen(true)}>
          Novo Cliente
        </Button>
      </div>
      <DataTable columns={columns} data={clients || []} actions={actions} />
      
      <NewClientDialog
        open={isNewClientDialogOpen}
        onOpenChange={setIsNewClientDialogOpen}
        onSubmit={handleAddClient}
      />

      {/* Client Details Panel */}
      <ClientDetailsPanel
        clientId={selectedClient?.id || null}
        open={isDetailsPanelOpen}
        onOpenChange={setIsDetailsPanelOpen}
        onEditClient={() => {
          setIsDetailsPanelOpen(false);
          setIsEditDialogOpen(true);
        }}
        onManageModules={() => {
          setIsDetailsPanelOpen(false);
          setIsModulesDialogOpen(true);
        }}
        onManageUsers={() => {
          setIsDetailsPanelOpen(false);
          setIsUsersDialogOpen(true);
        }}
      />

      {/* Edit Client Dialog */}
      <EditClientInlineDialog
        clientId={selectedClient?.id || null}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={() => refetch()}
      />

      {/* Client Modules Dialog */}
      <ClientModulesDialog
        clientId={selectedClient?.id || null}
        clientName={selectedClient?.name || ''}
        open={isModulesDialogOpen}
        onOpenChange={setIsModulesDialogOpen}
      />

      {/* Client Users Dialog */}
      <ClientUsersDialog
        clientId={selectedClient?.id || null}
        clientName={selectedClient?.name || ''}
        open={isUsersDialogOpen}
        onOpenChange={setIsUsersDialogOpen}
      />
    </div>
  );
};